/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useEffect, useMemo, useState} from "react"
import {Checkbox, Col, Row, Spin, Radio, Tabs} from "antd"
import {useActiveTab, useModel, useVisible} from "hooks"
import {TabHeader} from "components/Tab"
import {DailyAttendance} from "./DailyAttendance"
import dailyAttendanceStyles from "./DailyAttendance.module.css"
import {DailyChart} from "./DailyChart"
import {attendanceService} from "services"
import {handleError} from "helpers"
import {useQuery} from "@tanstack/react-query"
import moment from "moment"
import {DailyAttendanceShiftViewBy, DailyAttendanceViewType} from "types/attendance"
import CircularSpin from "components/CircularSpin"
import {RollCall} from "./RollCall/RollCall"
import {useRetentionStageSettings} from "hooks/useRetentionStageSettings"

const {TabPane} = Tabs

const DAILY_ATTENDANCE_TABS = {
    ALL: "all",
    DAILY: "daily",
    ROLL_CALL: "roll_call"
}

type Props = {
    studentProfileId?: number
}

export function DailyAttendanceTab(props: Props) {
    const model = useModel()

    const allStatsInfoVisible = useVisible(false)

    const {studentProfileId} = props
    const [viewType, setViewType] = useState<DailyAttendanceViewType>(DailyAttendanceViewType.ViewByProgram)
    const [shiftViewBy, setShiftViewBy] = useState<{
        byShift: boolean
        byProgram: boolean
        byCampus: boolean
    }>({
        byShift: true,
        byProgram: false,
        byCampus: false
    })
    const [filterAllDateRange, setFilterAllDateRange] = useState<string[]>([
        moment().startOf("week").format("YYYY-MM-DD"),
        moment().endOf("week").format("YYYY-MM-DD")
    ])
    const [filterDailyDateRange, setFilterDailyDateRange] = useState<string[]>([
        moment().format("YYYY-MM-DD"),
        moment().format("YYYY-MM-DD")
    ])
    const [filterRollCallDate, setFilterRollCallDate] = useState<string>(moment().format("YYYY-MM-DD"))
    const [activeTab, onChangeTab] = useActiveTab(DAILY_ATTENDANCE_TABS.ALL, "sub-type")
    const [filterCharts, setFilterCharts] = useState<{
        majorVersionIds: number[]
        phaseIds: number[]
        shiftIds: number[]
        campusIds: number[]
        programIds: number[]
        eventType?: string
    }>({
        majorVersionIds: [],
        phaseIds: [],
        shiftIds: [],
        campusIds: [],
        programIds: [],
        eventType: undefined
    })

    const {retentionStages: defaultStudentStatuses, isLoadingRetentionStageSetting} = useRetentionStageSettings({
        stages: ["stage1", "stage2", "stage3"]
    })

    const {isFetching: isFetchingStats, data: statsInfo} = useQuery(
        ["daily-attendance-stats", studentProfileId, viewType, shiftViewBy],
        () => {
            let shiftFilters = []
            const userCampuses = model.getCurrentUserProfile()?.campuses ?? []

            if (shiftViewBy.byShift) {
                shiftFilters = [DailyAttendanceShiftViewBy.Shift]
            } else {
                if (shiftViewBy.byProgram) {
                    shiftFilters.push(DailyAttendanceShiftViewBy.Program)
                }
                if (shiftViewBy.byCampus) {
                    shiftFilters.push(DailyAttendanceShiftViewBy.Campus)
                }
            }

            const campusIds = userCampuses.map((c) => c.id)

            return attendanceService.getDailyAttendanceStatsNew({
                viewType,
                campusIds,
                studentProfileIds: studentProfileId ? [studentProfileId] : [],
                shiftFilters,
                startDate: moment().format("YYYY-MM-DD"),
                endDate: moment().format("YYYY-MM-DD")
            })
        },
        {
            initialData: [],
            onError: (err) => {
                handleError(err)
            }
        }
    )

    const resetFilterCharts = useCallback(() => {
        setFilterCharts({
            majorVersionIds: [],
            phaseIds: [],
            shiftIds: [],
            campusIds: [],
            programIds: [],
            eventType: undefined
        })
    }, [])

    const onClickDailyChartItem = (item, type) => {
        if (
            viewType === DailyAttendanceViewType.ViewByPhase &&
            filterCharts.phaseIds.includes(item.phaseId) &&
            filterCharts.eventType === type
        ) {
            resetFilterCharts()
        } else if (
            viewType === DailyAttendanceViewType.ViewByShift &&
            filterCharts.eventType === type &&
            ((shiftViewBy.byShift && filterCharts.shiftIds.includes(item.shiftId)) ||
                (shiftViewBy.byProgram &&
                    shiftViewBy.byCampus &&
                    filterCharts.campusIds.includes(item.campusId) &&
                    filterCharts.programIds.includes(item.programId)) ||
                (shiftViewBy.byCampus && filterCharts.campusIds.includes(item.campusId)) ||
                (shiftViewBy.byProgram && filterCharts.programIds.includes(item.programId)))
        ) {
            resetFilterCharts()
        } else if (
            viewType === DailyAttendanceViewType.ViewByProgram &&
            filterCharts.majorVersionIds.includes(item.id) &&
            filterCharts.eventType === type
        ) {
            resetFilterCharts()
        } else {
            setFilterCharts({
                majorVersionIds: item.id ? [item.id] : [],
                eventType: type,
                phaseIds: item.phaseId ? [item.phaseId] : [],
                shiftIds: item.shiftId ? [item.shiftId] : [],
                campusIds: item.campusId ? [item.campusId] : [],
                programIds: item.programId ? [item.programId] : []
            })
        }
    }

    const onChangeViewType = useCallback(
        (viewType) => {
            setViewType(viewType)
            resetFilterCharts()
        },
        [viewType]
    )

    const renderStats = useMemo(() => {
        const statsInfoData = !allStatsInfoVisible.isVisible
            ? (statsInfo as any).filter((stat) => stat.activeStudentsCount > 0)
            : statsInfo
        return (
            <Row gutter={[40, 40]}>
                {isFetchingStats ? (
                    <div className={dailyAttendanceStyles.spinWrapper}>
                        <Spin />
                    </div>
                ) : (
                    statsInfoData.map((item) => (
                        <Col key={item.id || item.phaseId} span={8}>
                            <DailyChart
                                viewType={viewType}
                                data={item}
                                shiftViewBy={shiftViewBy}
                                filter={filterCharts}
                                onClickItem={onClickDailyChartItem}
                            />
                        </Col>
                    ))
                )}
            </Row>
        )
    }, [allStatsInfoVisible, statsInfo, filterCharts, viewType, shiftViewBy, isFetchingStats])

    return (
        <div>
            {!model.isStaffOrAdmin() ? null : isFetchingStats ? (
                <div className={dailyAttendanceStyles.chartLoading}>
                    <CircularSpin size="large" />
                </div>
            ) : (
                <>
                    <div className={dailyAttendanceStyles.checkbox}>
                        <div className={dailyAttendanceStyles.checkboxBlock}>
                            <Checkbox checked={allStatsInfoVisible.isVisible} onChange={allStatsInfoVisible.toggle}>
                                Show All
                            </Checkbox>

                            {viewType === DailyAttendanceViewType.ViewByShift && (
                                <div>
                                    <Checkbox
                                        checked={shiftViewBy.byShift}
                                        onChange={() =>
                                            setShiftViewBy({
                                                byShift: true,
                                                byProgram: false,
                                                byCampus: false
                                            })
                                        }>
                                        By Shift
                                    </Checkbox>
                                    <Checkbox
                                        checked={shiftViewBy.byProgram}
                                        onChange={() => {
                                            let byShift = false
                                            if (!!shiftViewBy.byProgram && !shiftViewBy.byCampus) {
                                                byShift = true
                                            }
                                            setShiftViewBy({
                                                byShift: byShift,
                                                byProgram: !shiftViewBy.byProgram,
                                                byCampus: shiftViewBy.byCampus
                                            })
                                        }}>
                                        By Program
                                    </Checkbox>
                                    <Checkbox
                                        checked={shiftViewBy.byCampus}
                                        onChange={() => {
                                            let byShift = false
                                            if (!shiftViewBy.byProgram && !!shiftViewBy.byCampus) {
                                                byShift = true
                                            }
                                            setShiftViewBy({
                                                byShift: byShift,
                                                byProgram: shiftViewBy.byProgram,
                                                byCampus: !shiftViewBy.byCampus
                                            })
                                        }}>
                                        By Campus
                                    </Checkbox>
                                </div>
                            )}
                        </div>
                        <Radio.Group
                            value={viewType}
                            onChange={(e) => {
                                if (e.target.value === DailyAttendanceViewType.ViewByShift) {
                                    setShiftViewBy({
                                        byShift: true,
                                        byProgram: false,
                                        byCampus: false
                                    })
                                }
                                onChangeViewType(e.target.value)
                            }}>
                            <Radio.Button
                                className={dailyAttendanceStyles.viewTypeButton}
                                value={DailyAttendanceViewType.ViewByProgram}>
                                View By Program
                            </Radio.Button>
                            <Radio.Button
                                className={dailyAttendanceStyles.viewTypeButton}
                                value={DailyAttendanceViewType.ViewByPhase}>
                                View By Phase
                            </Radio.Button>
                            <Radio.Button
                                className={dailyAttendanceStyles.viewTypeButton}
                                value={DailyAttendanceViewType.ViewByShift}>
                                View By Shift
                            </Radio.Button>
                        </Radio.Group>
                    </div>
                    {renderStats}
                </>
            )}
            <div className={dailyAttendanceStyles.tabContainer}>
                {isLoadingRetentionStageSetting ? (
                    <CircularSpin size="large" />
                ) : (
                    <Tabs
                        className="fullwidth klassSubTab"
                        style={{width: "100%"}}
                        activeKey={activeTab}
                        onChange={onChangeTab}>
                        <TabPane key={DAILY_ATTENDANCE_TABS.ALL} tab={<TabHeader title="All Punches" />}>
                            {activeTab === DAILY_ATTENDANCE_TABS.ALL && (
                                <DailyAttendance
                                    key={DAILY_ATTENDANCE_TABS.ALL}
                                    type={DAILY_ATTENDANCE_TABS.ALL}
                                    studentProfileId={studentProfileId}
                                    statsInfo={statsInfo}
                                    dateRange={filterAllDateRange}
                                    setDateRange={setFilterAllDateRange}
                                    filter={filterCharts}
                                    defaultStudentStatuses={defaultStudentStatuses}
                                    showAll
                                />
                            )}
                        </TabPane>
                        <TabPane key={DAILY_ATTENDANCE_TABS.DAILY} tab={<TabHeader title="Daily" />}>
                            {activeTab === DAILY_ATTENDANCE_TABS.DAILY && (
                                <DailyAttendance
                                    key={DAILY_ATTENDANCE_TABS.DAILY}
                                    type={DAILY_ATTENDANCE_TABS.DAILY}
                                    studentProfileId={studentProfileId}
                                    statsInfo={statsInfo}
                                    dateRange={filterDailyDateRange}
                                    setDateRange={setFilterDailyDateRange}
                                    defaultStudentStatuses={defaultStudentStatuses}
                                    filter={filterCharts}
                                />
                            )}
                        </TabPane>
                        {model.isStaffOrAdmin() ? (
                            <TabPane key={DAILY_ATTENDANCE_TABS.ROLL_CALL} tab={<TabHeader title="Roll Call" />}>
                                {activeTab === DAILY_ATTENDANCE_TABS.ROLL_CALL && (
                                    <RollCall
                                        defaultStudentStatuses={defaultStudentStatuses}
                                        studentProfileId={studentProfileId}
                                        statsInfo={statsInfo}
                                        date={filterRollCallDate}
                                        setDate={setFilterRollCallDate}
                                        filter={filterCharts}
                                    />
                                )}
                            </TabPane>
                        ) : null}
                    </Tabs>
                )}
            </div>
        </div>
    )
}

export {DAILY_ATTENDANCE_TABS}
